import {
  formatDateUtil,
  parseDate,
  formatDateWithTimezone,
  toJSTDateTime,
  setTimeForDate,
  getCurrentDate,
  addTimeToDate,
  subtractFromDate,
  toUtcTimestamp as toUtcTimestampUtil,
  getDateComponent,
  utcToSelectedTimezone,
  setHoursAndMinutes,
  addHoursWithTimezone,
  formatTimeComponent
} from "./dateFnsUtils";
import { IToUtcDateRage } from "../dto/utils/utcConversion.dto";

export const defaultFormat = "yyyy-MM-dd";
export const dateTimeFormat = "yyyy-MM-dd HH:mm:ss";
export const dateTimeFormatNoSecond = "yyyy-MM-dd HH:mm";

const DATE_FORMAT = "yyyy-MM-dd";
const DATE_TIME_FORMAT = `${DATE_FORMAT} HH:mm:ss`;

export const toUtcDateRage = (dateRange): IToUtcDateRage => {
  const { from_date, to_date } = dateRange;
  const start = formatDateWithTimezone(
    parseDate(from_date).toISOString(),
    defaultFormat,
    "UTC",
  );
  const end = formatDateWithTimezone(
    parseDate(to_date).toISOString(),
    defaultFormat,
    "UTC",
  );
  return { from_date: start, to_date: end };
};

export const toUtcTimestamp = (timestamp: string, format: string = defaultFormat) =>
  formatDateWithTimezone(parseDate(timestamp).toISOString(), format, "UTC");

export const toLocalDateTime = (timestamp, format = defaultFormat) => {
  // Add 'Z' to indicate UTC timezone
    const date = new Date(timestamp + 'Z');
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
        throw new Error('Invalid date format');
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    const dateTimeWithoutSeonds = `${year}-${month}-${day} ${hours}:${minutes}`;
    
    return format === dateTimeFormatNoSecond ? dateTimeWithoutSeonds : `${dateTimeWithoutSeonds}:${seconds}`;
};

// Your existing toJSTDateTime function can be used directly

export const toLocalTime = (hours, minutes) => {
  // Create a date with the specified UTC time
  const utcDate = setTimeForDate(getCurrentDate(), { hours, minutes, seconds: 0 });

  // Format in local time
  const localTimeStr = formatDateUtil(utcDate, dateTimeFormat);
  const localDate = parseDate(localTimeStr);

  return {
    hours: getDateComponent(localDate, "hours"),
    minutes: getDateComponent(localDate, "minutes"),
  };
};

export const toJSTTime = (hours, minutes) => {
  // Create a date with specified hours and minutes (treating as UTC)
  const currentDate = getCurrentDate();
  const utcDateTime = setHoursAndMinutes(currentDate, hours, minutes);
  
  // Convert UTC to JST using your utility (this adds 9 hours for JST timezone)
  const jstDate = addHoursWithTimezone(utcDateTime, 'JST');
  
  return {
    hours: getDateComponent(jstDate, 'hours'),
    minutes: getDateComponent(jstDate, 'minutes'),
  };
};

export const toUTCTime = (hours, minutes) => {
  // Create a date with specified hours and minutes (treating as JST)
  const currentDate = getCurrentDate();
  const jstDateTime = setHoursAndMinutes(currentDate, hours, minutes);

  // Convert JST to UTC by subtracting 9 hours
  const utcDate = addHoursWithTimezone(jstDateTime, 'UTC');

  return {
    hours: getDateComponent(utcDate, 'hours'),
    minutes: getDateComponent(utcDate, 'minutes'),
  };
};


export const formatDateTime = (value, timezone, format = DATE_TIME_FORMAT) => {
  if (!value || value === "-") return value;
  return utcToSelectedTimezone(value, format, timezone);
};

export const utcToJst = (date, format) =>
  formatDateUtil(addTimeToDate(parseDate(date), 9, "hours"), format);

export const jstToUtc = (date, format) =>
  formatDateUtil(subtractFromDate(parseDate(date), 9, "hours"), format);

export const utcToUtcFormat = (date, format) =>
  formatDateUtil(parseDate(date), format);


export const campaignMessageDeliveryWindowConverter = (campaign, timezone) => {
  const ranges = campaign["do_not_send_time_ranges"];

  if (!Array.isArray(ranges) || ranges.length === 0) {
    return [
      {
        fromTime: { hours: "00", minutes: "00" },
        toTime: { hours: "00", minutes: "00" },
      },
    ];
  }

  function formatTimeComponent(num) {
    return num.toString().padStart(2, "0");
  }

  return ranges.map((deniedTime) => {
    let fromTime, toTime;

    if (deniedTime["time_zone"] === timezone) {
      fromTime = {
        hours: deniedTime["from_hour"],
        minutes: deniedTime["from_minute"],
      };
      toTime = { hours: deniedTime["to_hour"], minutes: deniedTime["to_minute"] };
    } else if (timezone === "JST") {
      fromTime = toJSTTime(deniedTime["from_hour"], deniedTime["from_minute"]);
      toTime = toJSTTime(deniedTime["to_hour"], deniedTime["to_minute"]);
    } else if (timezone === "UTC") {
      fromTime = toUTCTime(deniedTime["from_hour"], deniedTime["from_minute"]);
      toTime = toUTCTime(deniedTime["to_hour"], deniedTime["to_minute"]);
    }

    return {
      fromTime: {
        hours: formatTimeComponent(fromTime.hours),
        minutes: formatTimeComponent(fromTime.minutes),
      },
      toTime: {
        hours: formatTimeComponent(toTime.hours),
        minutes: formatTimeComponent(toTime.minutes),
      },
    };
  });
};